import { Signal, Waves } from "lucide-react";

/* Aurora Arc — Founder-approved Option 1 (production design authority) */

const AURORA: [number, string][] = [
  [0, "#7B2CFF"], [0.28, "#A53FFF"], [0.5, "#435CFF"], [0.74, "#168DFF"], [1, "#05D1E5"],
];

const hex2rgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

export const auroraAt = (t: number) => {
  const x = Math.max(0, Math.min(t, 1));
  let i = 0;
  while (i < AURORA.length - 2 && x > AURORA[i + 1][0]) i++;
  const [t0, c0] = AURORA[i];
  const [t1, c1] = AURORA[i + 1];
  const f = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
  const a = hex2rgb(c0), b = hex2rgb(c1);
  return `rgb(${Math.round(a[0] + (b[0] - a[0]) * f)},${Math.round(a[1] + (b[1] - a[1]) * f)},${Math.round(a[2] + (b[2] - a[2]) * f)})`;
};

const CX = 110, CY = 104, R = 78;
const ang = (v: number) => 135 + v * 2.7;
const pol = (v: number, r: number): [number, number] => {
  const a = (ang(v) * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
};
const arcPath = (v0: number, v1: number, r: number) => {
  const [x0, y0] = pol(v0, r);
  const [x1, y1] = pol(v1, r);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${(v1 - v0) * 2.7 > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};

const SEG_N = 72;
const SEGS = Array.from({ length: SEG_N }, (_, i) => {
  const v0 = (i / SEG_N) * 100;
  const v1 = ((i + 1) / SEG_N) * 100;
  return { v0, d: arcPath(v0, Math.min(v1 + 0.55, 100), R), color: auroraAt((v0 + v1) / 200) };
});

const MINOR = Array.from({ length: 51 }, (_, i) => i * 2)
  .filter((v) => v % 25 !== 0)
  .map((v) => {
    const [x1, y1] = pol(v, R - 13);
    const [x2, y2] = pol(v, R - 9);
    return { v, x1, y1, x2, y2 };
  });

const MAJOR = [0, 25, 50, 75, 100].map((v) => {
  const [x1, y1] = pol(v, R - 16.5);
  const [x2, y2] = pol(v, R - 9);
  return { v, x1, y1, x2, y2 };
});

const LABELS = [0, 25, 50, 75, 100].map((v) => {
  const [x, y] = pol(v, 96.5);
  return { v, x, y: y + 3 };
});

export const AuroraGauge = ({ pct, num, label, labelColor, delta, done, animating, glint, dormant, compact = false }: {
  pct: number; num: string | number; label?: string; labelColor?: string;
  delta?: number | null; done: boolean; animating: boolean; glint: boolean; dormant?: boolean; compact?: boolean;
}) => {
  const p = Math.max(0, Math.min(pct, 100));
  const [nx, ny] = pol(p, R);
  const active = SEGS.filter((s) => s.v0 < p);
  const dcolor = delta != null && delta > 0 ? "text-[#34D399]" : delta != null && delta < 0 ? "text-[#F87171]" : "text-gray-500";
  return (
    <div className="relative w-full" aria-hidden>
      <svg viewBox="0 0 220 188" className="w-full">
        <defs>
          <filter id="aur-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4.2" />
          </filter>
          <filter id="aur-soft" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.8" />
          </filter>
        </defs>

        {/* recessed track: dark outer shadow + rail + faint outer edge */}
        <path d={arcPath(0, 100, R)} fill="none" stroke="#000" strokeOpacity="0.5" strokeWidth="15" strokeLinecap="round" />
        <path d={arcPath(0, 100, R)} fill="none" stroke="#101320" strokeWidth="10" strokeLinecap="round" />
        <path d={arcPath(0, 100, R + 5.6)} fill="none" stroke="#fff" strokeOpacity="0.04" strokeWidth="1" />

        {/* aurora glow bed behind the crisp arc */}
        {!dormant && active.length > 0 && (
          <g filter="url(#aur-glow)" opacity="0.4">
            {active.map((s, i) => (
              <path key={i} d={s.d} fill="none" stroke={s.color} strokeWidth="13" strokeLinecap="round" />
            ))}
          </g>
        )}

        {/* crisp gradient arc following the geometry */}
        {!dormant && active.map((s, i) => (
          <path key={i} d={s.d} fill="none" stroke={s.color} strokeWidth="9" strokeLinecap="round" />
        ))}

        {/* thin glass highlight along the inner edge */}
        {!dormant && p > 2 && (
          <path d={arcPath(0.6, Math.max(p - 0.6, 1), R - 2.6)} fill="none" stroke="#fff"
            strokeOpacity="0.26" strokeWidth="1.3" strokeLinecap="round" />
        )}

        {/* leading-edge light during the reveal sweep */}
        {animating && p > 3 && (
          <path d={arcPath(Math.max(0, p - 6), p, R)} fill="none" stroke="#fff" strokeWidth="9"
            opacity="0.25" filter="url(#aur-soft)" strokeLinecap="round" />
        )}

        {/* occasional micro-sheen — light catching the glass */}
        {!dormant && done && p > 20 && (
          <path className="nl-arc-sheen" d={arcPath(p * 0.3, p * 0.62, R)} fill="none" stroke="#fff"
            strokeWidth="6" strokeLinecap="round" filter="url(#aur-soft)" style={{ opacity: 0 }} />
        )}

        {/* tick system */}
        {!compact && MINOR.map((t) => (
          <line key={t.v} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="#8b93ad"
            strokeWidth="0.8" opacity={dormant ? 0.07 : t.v <= p ? 0.34 : 0.12} />
        ))}
        {MAJOR.map((t) => (
          <line key={t.v} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={!dormant && t.v <= p ? "#cdd5ea" : "#5b6379"} strokeWidth="1.4"
            opacity={dormant ? 0.25 : t.v <= p ? 0.9 : 0.5} />
        ))}

        {/* scale labels */}
        {LABELS.map((l) => (
          <text key={l.v} x={l.x} y={l.y} textAnchor="middle" fontSize={compact ? 12 : 9.5}
            fontFamily="'Geist Mono','JetBrains Mono',monospace"
            fill={!dormant && l.v <= p ? "#b3bcd4" : "#79829c"}>
            {l.v}
          </text>
        ))}

        {/* current score node */}
        {!dormant && p > 0.5 && (
          <g>
            <circle cx={nx} cy={ny} r="10" fill="#05D1E5" opacity="0.22" filter="url(#aur-glow)" />
            <circle cx={nx} cy={ny} r="5" fill="#9BEFFC" opacity="0.3" filter="url(#aur-soft)" />
            <circle cx={nx} cy={ny} r="2.3" fill="#fff" />
            {!compact && (
              <g transform={`translate(${nx} ${ny})`} opacity="0.85">
                <path d="M 0 -6 L 0.8 -0.8 L 6 0 L 0.8 0.8 L 0 6 L -0.8 0.8 L -6 0 L -0.8 -0.8 Z" fill="#fff" opacity="0.55" />
              </g>
            )}
          </g>
        )}

        {/* restrained pulse at sweep completion */}
        {glint && (
          <g className="lic-glint">
            <circle cx={nx} cy={ny} r="8" fill="#05D1E5" opacity="0.45" filter="url(#aur-soft)" />
            <circle cx={nx} cy={ny} r="2.6" fill="#fff" />
          </g>
        )}
      </svg>

      {/* central score */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ transform: "translateY(4%)" }}>
        <span data-testid="layer-index-value"
          className={`nl-sg font-bold leading-none ${compact ? "text-[25px]" : "text-[42px]"} text-[#f4f7ff]`}
          style={{ textShadow: "0 2px 18px rgba(6,10,24,0.9), 0 0 26px rgba(90,120,255,0.14)" }}>
          {num}
        </span>
        <span className={`nl-mono ${compact ? "text-[8px] mt-0.5" : "text-[11.5px] mt-1"} text-[#7d869e]`}>/100</span>
        {label && (
          <p data-testid="layer-index-label" key={label}
            className={`${done ? "lic-fade" : "opacity-0"} nl-mono font-bold uppercase ${compact ? "text-[7.5px] tracking-[0.16em] mt-0.5" : "text-[10.5px] tracking-[0.22em] mt-1"}`}
            style={{ color: labelColor }}>
            {label}
          </p>
        )}
        {delta != null && (
          <p data-testid="layer-index-delta"
            className={`transition-opacity duration-500 ${done ? "opacity-100" : "opacity-0"} nl-mono ${compact ? "text-[7px] mt-0.5" : "text-[10px] mt-1"} flex items-center gap-1`}
            style={{ transitionDelay: done ? "140ms" : "0ms" }}>
            <span className={dcolor}>{delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} {Math.abs(delta)} pts</span>
            <span className="text-gray-600">· 24h</span>
          </p>
        )}
      </div>
    </div>
  );
};

/* ---------------- History sparkline (real Layer Index history) ---------------- */

export const IndexSparkline = ({ history }: { history: { date: string; value: number }[] }) => {
  if (history.length < 2) return null;
  const W = 220, H = 26;
  const vals = history.map((h) => h.value);
  const max = Math.max(...vals), min = Math.min(...vals);
  const span = Math.max(max - min, 6);
  const mid = (max + min) / 2;
  const y = (v: number) => H - 3 - ((v - (mid - span / 2)) / span) * (H - 6);
  const x = (i: number) => (i / (history.length - 1)) * (W - 4) + 2;
  const pts = history.map((h, i) => `${x(i).toFixed(1)},${y(h.value).toFixed(1)}`).join(" ");
  const lx = x(history.length - 1), ly = y(vals[vals.length - 1]);
  return (
    <div data-testid="layer-index-sparkline" aria-hidden>
      <div className="flex items-center justify-between mb-1">
        <span className="nl-mono text-[9px] uppercase tracking-[0.22em] text-[#8a92a8]">Index history</span>
        <span className="nl-mono text-[9px] uppercase tracking-widest text-[#6b7488]">{history.length}d</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[26px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="nl-aur-spark" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#7B2CFF" />
            <stop offset="0.5" stopColor="#435CFF" />
            <stop offset="1" stopColor="#05D1E5" />
          </linearGradient>
          <linearGradient id="nl-aur-sparkfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#435CFF" stopOpacity="0.16" />
            <stop offset="1" stopColor="#435CFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`${pts} ${W - 2},${H} 2,${H}`} fill="url(#nl-aur-sparkfill)" />
        <polyline points={pts} fill="none" stroke="url(#nl-aur-spark)" strokeWidth="1.4"
          strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lx} cy={ly} r="3.6" fill="#05D1E5" opacity="0.25" />
        <circle cx={lx} cy={ly} r="1.8" fill="#05D1E5" />
      </svg>
    </div>
  );
};

/* ---------------- Signal tiles (real sub-components only) ---------------- */

const stateOf = (v: number): [string, string] =>
  v >= 70 ? ["Strong", "#34D399"] : v >= 55 ? ["Firm", "#6EE7B7"] : v >= 45 ? ["Neutral", "#A3A3A3"]
    : v >= 30 ? ["Soft", "#F87171"] : ["Weak", "#EF4444"];

export const SignalTiles = ({ components }: { components: any }) => {
  const c = components || {};
  const tiles = [
    c.momentum != null && { key: "momentum", label: "Momentum", icon: Waves, accent: "#A855F7", value: Math.round(c.momentum), state: stateOf(c.momentum) },
    c.sentiment != null && { key: "sentiment", label: "Sentiment", icon: Signal, accent: "#60A5FA", value: Math.round(c.sentiment), state: stateOf(c.sentiment) },
  ].filter(Boolean) as { key: string; label: string; icon: any; accent: string; value: number; state: [string, string] }[];
  if (!tiles.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="layer-index-tiles">
      {tiles.map((t) => (
        <div key={t.key} data-testid={`layer-index-tile-${t.key}`}
          className="min-w-0 rounded-lg border border-white/[0.07] bg-[#07080d]/80 px-2.5 py-2.5 transition-colors duration-200 hover:border-white/[0.14]">
          <span className="flex h-[21px] w-[21px] items-center justify-center rounded-[6px]"
            style={{ background: `${t.accent}22`, boxShadow: `inset 0 0 0 1px ${t.accent}3d` }}>
            <t.icon className="h-[11px] w-[11px]" style={{ color: t.accent }} />
          </span>
          <p className="nl-geist text-[10.5px] font-medium text-[#b6bdcf] truncate mt-1.5">{t.label}</p>
          <div className="mt-1 flex items-baseline justify-between gap-1">
            <span className="nl-sg text-[19px] font-bold leading-none text-[#f4f7ff]">{t.value}</span>
            <span className="nl-mono text-[9.5px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: t.state[1] }}>
              {t.state[0]}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Aurora atmosphere (restrained, layered) ---------------- */

const STARS = [
  { top: "16%", left: "12%", o: 0.2, s: 1.5 },
  { top: "24%", left: "84%", o: 0.26, s: 2 },
  { top: "12%", left: "62%", o: 0.14, s: 1 },
  { top: "38%", left: "8%", o: 0.12, s: 1 },
  { top: "31%", left: "92%", o: 0.16, s: 1.5 },
  { top: "45%", left: "78%", o: 0.1, s: 1 },
];

export const AuroraAtmosphere = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#7C3AED]/45 to-[#05D1E5]/25" />
    <div className="absolute -top-10 -left-8 h-56 w-56 rounded-full"
      style={{ background: "radial-gradient(circle, rgba(123,44,255,0.15), transparent 65%)" }} />
    <div className="absolute top-6 right-[-40px] h-48 w-48 rounded-full"
      style={{ background: "radial-gradient(circle, rgba(22,141,255,0.11), transparent 65%)" }} />
    <div className="absolute top-14 right-1 h-24 w-24 rounded-full"
      style={{ background: "radial-gradient(circle, rgba(5,209,229,0.13), transparent 70%)" }} />
    <div className="absolute top-24 -left-6 h-44 w-24 rotate-[24deg] blur-2xl"
      style={{ background: "linear-gradient(200deg, rgba(123,44,255,0.12), transparent 72%)" }} />
    <div className="absolute top-14 -right-5 h-48 w-24 -rotate-[18deg] blur-2xl"
      style={{ background: "linear-gradient(160deg, rgba(5,209,229,0.1), transparent 72%)" }} />
    {STARS.map((st, i) => (
      <span key={i} className="absolute rounded-full bg-white"
        style={{ top: st.top, left: st.left, width: st.s, height: st.s, opacity: st.o }} />
    ))}
    <div className="absolute inset-x-0 bottom-0 h-36"
      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5), transparent)" }} />
    <div className="absolute inset-0"
      style={{ background: "radial-gradient(ellipse at 50% 28%, transparent 52%, rgba(0,0,0,0.42) 100%)" }} />
    <div className="absolute inset-0 rounded-xl"
      style={{ boxShadow: "inset 0 0 42px -22px rgba(124,58,237,0.32), inset -14px 24px 60px -50px rgba(5,209,229,0.38)" }} />
    <div className="absolute inset-px rounded-[11px] border border-white/[0.04]" />
  </div>
);
